import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Activity,
  AlertCircle,
  Archive,
  ArrowLeft,
  ArrowUp,
  Bot,
  Brain,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleStop,
  Code2,
  Copy,
  FileDiff,
  Folder,
  Gauge,
  LoaderCircle,
  Mic,
  Monitor,
  Moon,
  MoreHorizontal,
  Package,
  Paperclip,
  Pin,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Shield,
  Sparkles,
  Sun,
  Terminal,
  Wifi,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import {
  ApiClient,
  ApiError,
  type Approval,
  type Artifact,
  type ChangeSummary,
  type EffectiveSettings,
  type Host,
  type ModelOption,
  type PermissionProfile,
  type Project,
  type Session,
  type Task,
  type TaskSnapshot,
  type TerminalPage,
  type Theme,
  type TimelineItem,
  type WireEvent,
} from "./lib/api";
import "./styles/app.css";

type Route =
  | { page: "home" }
  | { page: "pair" }
  | { page: "project"; hostId: string; projectId: string }
  | { page: "task"; hostId: string; taskId: string }
  | { page: "new"; hostId?: string | undefined; projectId?: string | undefined };

type ConnectionState = "connecting" | "online" | "offline" | "demo";
type InspectorKind = "changes" | "terminal" | "artifacts" | null;
type SettingsTab = "model" | "speed" | "reasoning" | "permission";

const client = new ApiClient();

function parseRoute(pathname = window.location.pathname): Route {
  const hashPath = window.location.hash.startsWith("#/") ? window.location.hash.slice(1).split("?")[0] : "";
  if (hashPath === "/pair") return { page: "pair" };
  const part = pathname.split("/").filter(Boolean);
  if (part[0] === "pair") return { page: "pair" };
  if (part[0] === "projects" && part[1] && part[2]) return { page: "project", hostId: decodeURIComponent(part[1]), projectId: decodeURIComponent(part[2]) };
  if (part[0] === "tasks" && part[1] && part[2]) return { page: "task", hostId: decodeURIComponent(part[1]), taskId: decodeURIComponent(part[2]) };
  if (part[0] === "new") {
    const query = new URLSearchParams(window.location.search);
    return { page: "new", hostId: query.get("host") ?? undefined, projectId: query.get("project") ?? undefined };
  }
  return { page: "home" };
}

function href(route: Route): string {
  if (route.page === "pair") return "/pair";
  if (route.page === "project") return `/projects/${encodeURIComponent(route.hostId)}/${encodeURIComponent(route.projectId)}`;
  if (route.page === "task") return `/tasks/${encodeURIComponent(route.hostId)}/${encodeURIComponent(route.taskId)}`;
  if (route.page === "new") {
    const query = new URLSearchParams();
    if (route.hostId) query.set("host", route.hostId);
    if (route.projectId) query.set("project", route.projectId);
    return `/new${query.size ? `?${query}` : ""}`;
  }
  return "/";
}

function formatRelative(value?: string | undefined): string {
  if (!value) return "尚未同步";
  const delta = Date.now() - new Date(value).getTime();
  if (Number.isNaN(delta)) return "刚刚";
  if (delta < 60_000) return "刚刚";
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)} 分钟前`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)} 小时前`;
  return `${Math.floor(delta / 86_400_000)} 天前`;
}

function statusText(task: Task): string {
  switch (task.runtimeState) {
    case "running": return "正在工作";
    case "waiting_approval": return "需要确认";
    case "waiting_input": return "等待回复";
    case "error": return "需要重试";
    case "not_loaded": return "尚未加载";
    default: return task.lastOutcome === "interrupted" ? "已停止" : task.lastOutcome === "failed" ? "未完成" : "空闲";
  }
}

function isActive(task?: Task | undefined): boolean {
  return task?.runtimeState === "running" || task?.runtimeState === "waiting_approval" || task?.runtimeState === "waiting_input";
}

function safeInitial(theme: Theme): Theme {
  if (theme !== "system") return theme;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export default function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute());
  const [session, setSession] = useState<Session | null>(null);
  const [bootError, setBootError] = useState<string>();
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem("relay-theme") as Theme) || "system");
  const [toast, setToast] = useState<string>();

  const navigate = useCallback((next: Route, replace = false) => {
    const target = href(next);
    if (replace) window.history.replaceState({}, "", target);
    else window.history.pushState({}, "", target);
    setRoute(next);
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const reloadSession = useCallback(async () => {
    setBootError(undefined);
    try {
      setSession(await client.getSession());
    } catch (error) {
      setBootError(readableError(error));
      setSession({ authenticated: false });
    }
  }, []);

  useEffect(() => {
    void reloadSession();
    const popstate = () => setRoute(parseRoute());
    window.addEventListener("popstate", popstate);
    return () => window.removeEventListener("popstate", popstate);
  }, [reloadSession]);

  useEffect(() => {
    const actual = safeInitial(theme);
    document.documentElement.dataset.theme = actual;
    localStorage.setItem("relay-theme", theme);
  }, [theme]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => document.documentElement.style.setProperty("--visual-viewport-height", `${viewport?.height ?? window.innerHeight}px`);
    update();
    viewport?.addEventListener("resize", update);
    window.addEventListener("resize", update);
    return () => {
      viewport?.removeEventListener("resize", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(undefined), 3600);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  if (session === null) return <BootScreen error={bootError} onRetry={reloadSession} />;
  if (!session.authenticated || route.page === "pair") {
    return <PairScreen api={client} session={session} onDone={(next) => { setSession(next); navigate({ page: "home" }, true); }} onBack={() => navigate({ page: "home" })} />;
  }

  return (
    <main className="app-frame">
      {route.page === "home" && <HomeScreen api={client} session={session} theme={theme} onTheme={setTheme} navigate={navigate} notify={setToast} />}
      {route.page === "project" && <ProjectScreen api={client} route={route} navigate={navigate} notify={setToast} />}
      {route.page === "task" && <TaskScreen api={client} route={route} navigate={navigate} notify={setToast} />}
      {route.page === "new" && <NewTaskScreen api={client} route={route} navigate={navigate} notify={setToast} />}
      {toast && <div className="toast" role="status"><CheckCircle2 size={16} />{toast}</div>}
    </main>
  );
}

function BootScreen({ error, onRetry }: { error?: string | undefined; onRetry: () => void }) {
  return <main className="boot-screen">
    <div className="brand-mark"><Sparkles size={22} /></div>
    <div className="boot-copy"><strong>正在打开远程工作区</strong><span>{error ?? "正在检查此浏览器的连接。"}</span></div>
    {error ? <button className="button secondary" onClick={onRetry}><RefreshCw size={16} />重试</button> : <LoaderCircle className="spin" size={22} />}
  </main>;
}

function PairScreen({ api, session, onDone, onBack }: { api: ApiClient; session: Session; onDone: (session: Session) => void; onBack: () => void }) {
  const [label, setLabel] = useState(session.deviceLabel ?? "我的手机");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const pairLink = useMemo(() => {
    const raw = window.location.hash.startsWith("#/") ? window.location.hash.slice(2) : "";
    const query = raw.startsWith("pair?") ? new URLSearchParams(raw.slice("pair?".length)) : new URLSearchParams(window.location.search);
    return { hostId: query.get("host") ?? "", token: query.get("token") ?? "" };
  }, []);

  const connect = async () => {
    if (!pairLink.hostId || !pairLink.token) {
      setError("请先在电脑上运行 bridge pair，再用手机扫描生成的二维码。");
      return;
    }
    setBusy(true); setError(undefined);
    try {
      onDone(await api.claimPair(pairLink.hostId, pairLink.token, label.trim() || "此浏览器"));
    } catch (nextError) { setError(readableError(nextError)); }
    finally { setBusy(false); }
  };
  return <main className="pair-screen">
    <div className="pair-header">
      {session.authenticated && <button className="icon-button" aria-label="返回" onClick={onBack}><ArrowLeft size={19} /></button>}
      <div className="brand-lockup"><span className="brand-mark small"><Sparkles size={16} /></span><span>Remote workspace</span></div>
    </div>
    <section className="pair-content">
      <div className="pair-orbit" aria-hidden="true"><Monitor size={42} /><span /><span /><span /></div>
      <p className="eyebrow">安全连接</p>
      <h1>连接一台电脑</h1>
      <p className="lead">此页面只建立远控授权。模型登录与本地项目凭据始终留在你的电脑上。</p>
      <label className="field-label">此设备的名称<input value={label} maxLength={48} onChange={(event) => setLabel(event.target.value)} placeholder="例如：我的手机" /></label>
      {pairLink.hostId && pairLink.token ? <div className="verification-card"><span>已读取本机生成的一次性配对链接</span><strong>有效期 5 分钟 · 仅限此浏览器</strong></div> : <p className="quiet-copy">在电脑上运行 <code>codexapp-bridge pair</code>，再用手机扫描生成的二维码。</p>}
      <button className="button primary full" disabled={busy || !pairLink.hostId || !pairLink.token} onClick={connect}>{busy ? <LoaderCircle className="spin" size={17} /> : <Wifi size={17} />}连接这台电脑</button>
      {error && <InlineError text={error} />}
      {api.usingMock && <p className="demo-note"><Activity size={14} />当前为本地演示数据；部署 API 后会自动使用真实连接。</p>}
    </section>
  </main>;
}

function HomeScreen({ api, session, theme, onTheme, navigate, notify }: {
  api: ApiClient; session: Session; theme: Theme; onTheme: (theme: Theme) => void; navigate: (route: Route) => void; notify: (text: string) => void;
}) {
  const [hosts, setHosts] = useState<Host[]>();
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [error, setError] = useState<string>();
  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hostOpen, setHostOpen] = useState(false);
  const [query, setQuery] = useState("");

  const reload = useCallback(async () => {
    setError(undefined);
    try {
      const loadedHosts = await api.getHosts();
      setHosts(loadedHosts);
      const chosen = loadedHosts.find((host) => host.presence !== "offline") ?? loadedHosts[0];
      if (!chosen) { setProjects([]); setTasks([]); return; }
      const [loadedProjects, loadedTasks] = await Promise.all([api.getProjects(chosen.hostId), api.getTasks(chosen.hostId)]);
      setProjects(loadedProjects); setTasks(loadedTasks.items);
    } catch (nextError) { setError(readableError(nextError)); }
  }, [api]);
  useEffect(() => { void reload(); }, [reload]);

  const visibleTasks = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return tasks;
    return tasks.filter((task) => `${task.title} ${task.preview ?? ""}`.toLocaleLowerCase().includes(needle));
  }, [query, tasks]);
  const primaryHost = hosts?.find((host) => host.presence === "online") ?? hosts?.[0];

  return <>
    <header className="topbar home-topbar">
      <button className="brand-button" onClick={() => setHostOpen(true)} aria-label="查看已连接电脑"><span className="brand-mark small"><Sparkles size={16} /></span><span>Remote</span><ChevronDown size={15} /></button>
      <div className="top-actions">
        <button className="icon-button" aria-label="搜索任务" onClick={() => setSearchOpen(true)}><Search size={19} /></button>
        <button className="avatar-button" aria-label="显示外观设置" onClick={() => setSettingsOpen(true)}>{session.user?.initials ?? session.user?.name?.slice(0, 1) ?? "我"}</button>
      </div>
    </header>
    <section className="home-content page-content">
      <div className="connection-line" onClick={() => setHostOpen(true)} role="button" tabIndex={0} onKeyDown={(event) => event.key === "Enter" && setHostOpen(true)}>
        <PresenceDot presence={primaryHost?.presence ?? "offline"} />
        <span>{primaryHost ? `${primaryHost.name} · ${primaryHost.presence === "online" ? "已连接" : primaryHost.presence === "stale" ? "正在重新连接" : "电脑离线"}` : "尚未连接电脑"}</span>
        <ChevronRight size={16} />
      </div>
      {api.usingMock && <div className="demo-strip"><Activity size={14} />预览模式 · 页面正在使用本地演示数据</div>}
      {error && <InlineError text={error} retry={reload} />}
      <section className="section-block project-section">
        <div className="section-heading"><div><p className="eyebrow">项目</p><h1>继续工作</h1></div><button className="text-action" onClick={() => primaryHost && navigate({ page: "new", hostId: primaryHost.hostId })}><Plus size={17} />新任务</button></div>
        {hosts === undefined ? <ListSkeleton rows={3} /> : projects.length ? <div className="project-rail">
          {projects.map((project) => <button className="project-tile" key={project.projectId} onClick={() => navigate({ page: "project", hostId: project.hostId, projectId: project.projectId })}>
            <span className="project-icon"><Folder size={18} /></span><span className="project-name">{project.name}</span><span className="project-path">{project.branch ? `${project.branch} · ` : ""}{project.taskCount ?? ""} 个任务</span>
          </button>)}
        </div> : <EmptyState title="还没有可访问的项目" text="在电脑上授权一个项目后，它会显示在这里。" />}
      </section>
      <section className="section-block recent-section">
        <div className="section-heading"><div><p className="eyebrow">最近</p><h2>任务</h2></div>{tasks.length > 4 && <button className="text-action" onClick={() => setSearchOpen(true)}>查看全部</button>}</div>
        {hosts === undefined ? <ListSkeleton rows={4} /> : visibleTasks.length ? <div className="task-list">{visibleTasks.slice(0, query ? 20 : 6).map((task) => <TaskRow task={task} key={task.taskId} onClick={() => navigate({ page: "task", hostId: task.hostId, taskId: task.taskId })} />)}</div> : <EmptyState title={query ? "没有匹配的任务" : "还没有任务"} text={query ? "试试修改关键词。" : "从一个项目开始新的任务。"} />}
      </section>
    </section>
    <BottomNav active="home" onHome={() => undefined} onSearch={() => setSearchOpen(true)} onNew={() => navigate({ page: "new", hostId: primaryHost?.hostId })} />
    {searchOpen && <SearchSheet api={api} hosts={hosts ?? []} initialTasks={tasks} onClose={() => setSearchOpen(false)} onTask={(task) => { setSearchOpen(false); navigate({ page: "task", hostId: task.hostId, taskId: task.taskId }); }} />}
    {hostOpen && <HostSheet hosts={hosts ?? []} onClose={() => setHostOpen(false)} onPair={() => navigate({ page: "pair" })} onRetry={reload} />}
    {settingsOpen && <AppearanceSheet theme={theme} onTheme={onTheme} onClose={() => setSettingsOpen(false)} onDone={() => notify("外观设置已更新")} />}
  </>;
}

function ProjectScreen({ api, route, navigate, notify }: { api: ApiClient; route: Extract<Route, { page: "project" }>; navigate: (route: Route) => void; notify: (text: string) => void }) {
  const [project, setProject] = useState<Project>();
  const [host, setHost] = useState<Host>();
  const [tasks, setTasks] = useState<Task[]>();
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string>();
  const [moreOpen, setMoreOpen] = useState(false);

  const reload = useCallback(async () => {
    setError(undefined);
    try {
      const [nextHost, projects, nextTasks] = await Promise.all([api.getHost(route.hostId), api.getProjects(route.hostId), api.getTasks(route.hostId, { projectId: route.projectId, query })]);
      setHost(nextHost); setProject(projects.find((item) => item.projectId === route.projectId)); setTasks(nextTasks.items);
    } catch (nextError) { setError(readableError(nextError)); }
  }, [api, route.hostId, route.projectId, query]);
  useEffect(() => { void reload(); }, [reload]);

  return <>
    <header className="topbar detail-topbar"><button className="icon-button" aria-label="返回首页" onClick={() => window.history.length > 1 ? window.history.back() : navigate({ page: "home" })}><ArrowLeft size={20} /></button><div className="top-title"><span>{project?.name ?? "项目"}</span><small>{host?.name ?? "正在加载"}</small></div><button className="icon-button" aria-label="项目选项" onClick={() => setMoreOpen(true)}><MoreHorizontal size={20} /></button></header>
    <section className="page-content project-page">
      <div className="project-hero"><div className="project-icon large"><Folder size={23} /></div><div><h1>{project?.name ?? "项目"}</h1><p>{project?.displayPath ?? "正在读取授权项目"}</p></div></div>
      <div className="subtle-status"><PresenceDot presence={host?.presence ?? "offline"} />{host?.presence === "online" ? "电脑已连接" : "电脑离线，显示最近同步内容"}{project?.branch && <><span className="separator">·</span><Code2 size={14} />{project.branch}</>}</div>
      <div className="list-toolbar"><label className="search-field"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索任务标题" /><button aria-label="清除搜索" className={query ? "mini-clear visible" : "mini-clear"} onClick={() => setQuery("")}><X size={14} /></button></label><button className="compose-compact" aria-label="新任务" onClick={() => navigate({ page: "new", hostId: route.hostId, projectId: route.projectId })}><Plus size={19} /></button></div>
      {error && <InlineError text={error} retry={reload} />}
      {tasks === undefined ? <ListSkeleton rows={5} /> : tasks.length ? <div className="task-list project-task-list">{tasks.map((task) => <TaskRow key={task.taskId} task={task} onClick={() => navigate({ page: "task", hostId: task.hostId, taskId: task.taskId })} />)}</div> : <EmptyState title="在此项目开始新任务" text="任务会在已授权的电脑和项目中创建。" action="新任务" onAction={() => navigate({ page: "new", hostId: route.hostId, projectId: route.projectId })} />}
    </section>
    {moreOpen && <ProjectInfoSheet project={project} host={host} onClose={() => setMoreOpen(false)} onNew={() => { setMoreOpen(false); navigate({ page: "new", hostId: route.hostId, projectId: route.projectId }); }} onNotify={notify} />}
  </>;
}

function TaskScreen({ api, route, navigate, notify }: { api: ApiClient; route: Extract<Route, { page: "task" }>; navigate: (route: Route) => void; notify: (text: string) => void }) {
  const { hostId, taskId } = route;
  const [snapshot, setSnapshot] = useState<TaskSnapshot>();
  const [timeline, setTimeline] = useState<TimelineItem[]>();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [error, setError] = useState<string>();
  const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(null);
  const [inspector, setInspector] = useState<InspectorKind>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [stopPending, setStopPending] = useState(false);
  const socketRef = useRef<{ close: () => void; subscribe: (taskIds?: string[] | undefined, afterHostSeq?: number | undefined) => void } | undefined>(undefined);

  const reload = useCallback(async () => {
    setError(undefined);
    try {
      const [nextSnapshot, history, nextApprovals] = await Promise.all([api.getTask(hostId, taskId), api.getTimeline(hostId, taskId), api.getApprovals(taskId)]);
      setSnapshot(nextSnapshot); setTimeline(history.items); setApprovals(nextApprovals);
      setConnection(api.usingMock ? "demo" : nextSnapshot.host?.presence === "online" ? "connecting" : "offline");
    } catch (nextError) { setError(readableError(nextError)); }
  }, [api, hostId, taskId]);
  useEffect(() => { void reload(); }, [reload]);

  const acceptEvent = useCallback((event: WireEvent) => {
    if (event.taskId && event.taskId !== taskId) return;
    if (event.type === "host.presence") {
      const presence = event.payload?.presence;
      setConnection(presence === "online" ? "online" : "offline");
    }
    if (event.type === "item.started" || event.type === "item.delta" || event.type === "item.completed") {
      const raw = event.payload ?? {};
      const incoming: TimelineItem = { itemId: String(event.itemId ?? raw.itemId ?? `event-${Date.now()}`), taskId, kind: (raw.kind as TimelineItem["kind"]) ?? "assistant_message", text: String(raw.text ?? raw.content ?? ""), state: event.type === "item.delta" ? "streaming" : "complete", payload: raw };
      setTimeline((current) => {
        const previous = current ?? [];
        const index = previous.findIndex((item) => item.itemId === incoming.itemId);
        return index === -1 ? [...previous, incoming] : previous.map((item, position) => position === index ? { ...item, ...incoming, text: `${item.text ?? ""}${event.type === "item.delta" ? incoming.text ?? "" : ""}` } : item);
      });
    }
    if (event.type === "approval.created") void api.getApprovals(taskId).then(setApprovals).catch(() => undefined);
    if (event.type === "turn.completed" || event.type === "task.updated") void api.getTask(hostId, taskId).then(setSnapshot).catch(() => undefined);
  }, [api, hostId, taskId]);

  useEffect(() => {
    if (!snapshot?.host?.hostId || api.usingMock) return;
    const socket = api.openHostEvents(snapshot.host.hostId, {
      onOpen: () => { setConnection("online"); socket.subscribe([taskId], snapshot.baseSeq); },
      onEvent: acceptEvent,
      onClose: () => setConnection("offline"),
      onError: () => setConnection("offline"),
    });
    socketRef.current = socket;
    return () => socket.close();
  }, [acceptEvent, api, snapshot?.baseSeq, snapshot?.host?.hostId, taskId]);

  const task = snapshot?.task;
  const project = snapshot?.project;
  const host = snapshot?.host;
  const applySettings = async (next: EffectiveSettings) => {
    if (!task || !host || !project) return;
    try {
      await api.command(host.hostId, { operation: "task.settings", taskId: task.taskId, projectId: project.projectId, expectedRevision: task.revision, capabilityRevision: snapshot?.capabilityRevision, payload: { modelId: next.modelId, effort: next.effort, serviceTier: next.serviceTier, permissionProfileId: next.permissionProfileId } });
      setSnapshot((current) => current ? { ...current, settings: next } : current);
      notify("设置将在下一轮生效");
    } catch (nextError) { notify(readableError(nextError)); }
  };
  const send = async (text: string) => {
    if (!task || !host || !project) return false;
    try {
      await api.command(host.hostId, { operation: task.runtimeState === "running" ? "turn.steer" : "turn.start", taskId: task.taskId, projectId: project.projectId, expectedTurnId: task.activeTurnId, expectedRevision: task.revision, capabilityRevision: snapshot?.capabilityRevision, payload: { text } });
      setTimeline((current) => [...(current ?? []), { itemId: `local-${Date.now()}`, taskId: task.taskId, kind: "user_message", text, state: "complete", createdAt: new Date().toISOString() }]);
      setSnapshot((current) => current ? { ...current, task: { ...current.task, runtimeState: "running", updatedAt: new Date().toISOString() } } : current);
      return true;
    } catch (nextError) { setError(readableError(nextError)); return false; }
  };
  const stop = async () => {
    if (!task || !host || !project) return;
    setStopPending(true);
    try {
      await api.command(host.hostId, { operation: "turn.interrupt", taskId: task.taskId, projectId: project.projectId, expectedTurnId: task.activeTurnId, expectedRevision: task.revision, capabilityRevision: snapshot?.capabilityRevision, payload: {} });
      setSnapshot((current) => current ? { ...current, task: { ...current.task, runtimeState: "idle", lastOutcome: "interrupted" } } : current);
      notify("已请求停止，正在等待电脑确认");
    } catch (nextError) { setError(readableError(nextError)); }
    finally { setStopPending(false); }
  };
  const respond = async (approval: Approval, decision: string) => {
    if (!task || !host || !project) return;
    setApprovals((current) => current.map((item) => item.approvalId === approval.approvalId ? { ...item, state: "responding" } : item));
    try {
      await api.command(host.hostId, { operation: "approval.respond", taskId: task.taskId, projectId: project.projectId, expectedRevision: task.revision, capabilityRevision: snapshot?.capabilityRevision, payload: { approvalId: approval.approvalId, revision: approval.revision, decision } });
      setApprovals((current) => current.map((item) => item.approvalId === approval.approvalId ? { ...item, state: "resolved" } : item));
      setSnapshot((current) => current ? { ...current, task: { ...current.task, runtimeState: "running" } } : current);
      notify(decision === "approve" ? "已发送允许请求" : "已发送拒绝请求");
    } catch (nextError) {
      setApprovals((current) => current.map((item) => item.approvalId === approval.approvalId ? { ...item, state: "pending" } : item));
      setError(readableError(nextError));
    }
  };

  return <>
    <header className="topbar detail-topbar task-topbar"><button className="icon-button" aria-label="返回任务列表" onClick={() => window.history.length > 1 ? window.history.back() : project && host ? navigate({ page: "project", hostId: host.hostId, projectId: project.projectId }) : navigate({ page: "home" })}><ArrowLeft size={20} /></button><div className="top-title"><span>{task?.title ?? "正在加载任务"}</span><small>{project?.name ?? ""}</small></div><button className="icon-button" aria-label="任务选项" onClick={() => setMoreOpen(true)}><MoreHorizontal size={20} /></button></header>
    <section className="task-layout">
      {task && <div className="task-context"><button onClick={() => setMoreOpen(true)}><PresenceDot presence={connection === "online" || connection === "demo" ? "online" : "offline"} />{connection === "demo" ? "演示连接" : connection === "connecting" ? "正在同步" : connection === "online" ? host?.name ?? "电脑已连接" : "电脑暂时离线"}<ChevronRight size={14} /></button>{task.pendingApprovalCount ? <span className="request-count"><Shield size={13} />{task.pendingApprovalCount}</span> : null}</div>}
      {error && <InlineError text={error} retry={reload} />}
      {!timeline || !task ? <ThreadSkeleton /> : <ThreadTimeline items={timeline} approvals={approvals} onInspect={setInspector} onRespond={respond} onCopy={notify} />}
    </section>
    {task && project && host && <Composer task={task} settings={snapshot?.settings} host={host} project={project} disabled={connection === "offline"} stopping={stopPending} onSettings={setSettingsTab} onSend={send} onStop={() => { void stop(); }} onNotify={notify} />}
    {settingsTab && task && host && project && <SettingsSheet api={api} tab={settingsTab} host={host} project={project} task={task} settings={snapshot?.settings} profiles={snapshot?.permissionProfiles ?? []} capabilityRevision={snapshot?.capabilityRevision} onClose={() => setSettingsTab(null)} onSave={applySettings} />}
    {inspector && task && <InspectorSheet api={api} kind={inspector} task={task} onClose={() => setInspector(null)} onNotify={notify} />}
    {moreOpen && <TaskMoreSheet task={task} host={host} project={project} connection={connection} onClose={() => setMoreOpen(false)} onInspect={setInspector} onNavigate={(next) => { setMoreOpen(false); navigate(next); }} />}
  </>;
}

function NewTaskScreen({ api, route, navigate, notify }: { api: ApiClient; route: Extract<Route, { page: "new" }>; navigate: (route: Route) => void; notify: (text: string) => void }) {
  const [hosts, setHosts] = useState<Host[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [hostId, setHostId] = useState(route.hostId ?? "");
  const [projectId, setProjectId] = useState(route.projectId ?? "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    void api.getHosts().then((nextHosts) => {
      setHosts(nextHosts);
      const nextHost = hostId || nextHosts.find((host) => host.presence === "online")?.hostId || nextHosts[0]?.hostId || "";
      setHostId(nextHost);
    }).catch((nextError) => setError(readableError(nextError)));
  // hostId is deliberately not a dependency: loading hosts should not reset a user choice.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  useEffect(() => {
    if (!hostId) return;
    void api.getProjects(hostId).then((nextProjects) => {
      setProjects(nextProjects);
      if (!nextProjects.some((project) => project.projectId === projectId)) setProjectId(nextProjects[0]?.projectId ?? "");
    }).catch((nextError) => setError(readableError(nextError)));
  }, [api, hostId, projectId]);

  const host = hosts.find((item) => item.hostId === hostId);
  const project = projects.find((item) => item.projectId === projectId);
  const create = async () => {
    if (!host || !project || !text.trim()) return;
    setBusy(true); setError(undefined);
    try {
      const result = await api.command(host.hostId, { operation: "task.create", projectId: project.projectId, capabilityRevision: host.capabilityRevision, payload: { text: text.trim(), permissionProfileId: "ask", environment: "current" } });
      const createdTaskId = result.taskId;
      if (createdTaskId) navigate({ page: "task", hostId: host.hostId, taskId: createdTaskId });
      else { notify("创建请求已接收，正在等待电脑返回任务。" ); navigate({ page: "project", hostId: host.hostId, projectId: project.projectId }); }
    } catch (nextError) { setError(readableError(nextError)); }
    finally { setBusy(false); }
  };
  return <>
    <header className="topbar detail-topbar"><button className="icon-button" aria-label="返回" onClick={() => window.history.length > 1 ? window.history.back() : navigate({ page: "home" })}><ArrowLeft size={20} /></button><div className="top-title"><span>新任务</span><small>{host?.presence === "online" ? "已连接" : "选择运行电脑"}</small></div><span className="topbar-spacer" /></header>
    <section className="new-task-page page-content"><div className="new-prompt"><p className="eyebrow">新会话</p><h1>我们要处理什么？</h1><p>任务会在你选定的电脑和项目中开始。</p></div><button className="context-picker" onClick={() => setPickerOpen(true)}><span className="project-icon"><Folder size={18} /></span><span><strong>{project?.name ?? "选择项目"}</strong><small>{host?.name ?? "选择电脑"}{project?.displayPath ? ` · ${project.displayPath}` : ""}</small></span><ChevronRight size={18} /></button>{error && <InlineError text={error} />}</section>
    <div className="new-composer"><textarea value={text} onChange={(event) => setText(event.target.value)} placeholder="描述你希望完成的事情" aria-label="新任务内容" autoFocus /><div className="new-composer-footer"><span>{host?.presence === "online" ? <><Wifi size={14} />将发送到 {host.name}</> : <><WifiOff size={14} />需要一台已连接的电脑</>}</span><button className="send-button" disabled={busy || !text.trim() || host?.presence !== "online" || !project} onClick={create}>{busy ? <LoaderCircle className="spin" size={17} /> : <ArrowUp size={18} />}</button></div></div>
    {pickerOpen && <ProjectPicker hosts={hosts} projects={projects} hostId={hostId} projectId={projectId} onClose={() => setPickerOpen(false)} onSelect={(nextHost, nextProject) => { setHostId(nextHost); setProjectId(nextProject); setPickerOpen(false); }} />}
  </>;
}

function TaskRow({ task, onClick }: { task: Task; onClick: () => void }) {
  return <button className="task-row" onClick={onClick}>
    <span className={`task-state ${task.runtimeState}`}><Circle size={8} fill="currentColor" /></span>
    <span className="task-row-main"><span className="task-row-title">{task.pinned && <Pin size={13} fill="currentColor" />}{task.title}</span><span className="task-row-preview">{task.preview || statusText(task)}</span></span>
    <span className="task-row-meta"><span>{formatRelative(task.updatedAt)}</span>{task.pendingApprovalCount ? <Shield size={14} /> : task.unread ? <Circle size={8} fill="currentColor" /> : <ChevronRight size={16} />}</span>
  </button>;
}

function ThreadTimeline({ items, approvals, onInspect, onRespond, onCopy }: { items: TimelineItem[]; approvals: Approval[]; onInspect: (kind: InspectorKind) => void; onRespond: (approval: Approval, decision: string) => void; onCopy: (message: string) => void }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const priorCount = useRef(items.length);
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    if (atBottom) root.scrollTo({ top: root.scrollHeight, behavior: "smooth" });
    else if (items.length > priorCount.current) setNewCount((count) => count + items.length - priorCount.current);
    priorCount.current = items.length;
  }, [atBottom, items]);
  return <div className="thread-scroll" ref={scrollRef} onScroll={(event) => { const element = event.currentTarget; const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80; setAtBottom(bottom); if (bottom) setNewCount(0); }}>
    <button className="load-earlier" onClick={() => onCopy("历史分页将在连接后加载")}>加载更早消息</button>
    <div className="timeline">
      {items.map((item) => <TimelineEntry key={item.itemId} item={item} onInspect={onInspect} onCopy={onCopy} />)}
      {approvals.filter((approval) => approval.state !== "resolved").map((approval) => <ApprovalCard key={approval.approvalId} approval={approval} onRespond={onRespond} />)}
    </div>
    {!atBottom && newCount > 0 && <button className="new-messages" onClick={() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); setNewCount(0); }}><ArrowDownIcon />{newCount} 条新内容</button>}
  </div>;
}

function TimelineEntry({ item, onInspect, onCopy }: { item: TimelineItem; onInspect: (kind: InspectorKind) => void; onCopy: (message: string) => void }) {
  if (item.kind === "user_message") return <article className="message user-message"><RichText text={item.text ?? ""} onCopy={onCopy} /></article>;
  if (item.kind === "assistant_message") return <article className="message assistant-message"><div className="assistant-label"><Bot size={15} />助手{item.state === "streaming" && <span className="stream-indicator"><i /><i /><i /></span>}</div><RichText text={item.text ?? ""} onCopy={onCopy} /></article>;
  if (item.kind === "reasoning_summary" || item.kind === "plan") return <details className="process-row"><summary><Brain size={15} /><span>{item.kind === "plan" ? "计划更新" : "处理过程"}</span><span className="process-summary">{item.text}</span><ChevronRight size={15} /></summary><p>{item.text}</p></details>;
  if (item.kind === "command" || item.kind === "tool_call" || item.kind === "tool_result") return <button className="tool-row" onClick={() => onInspect("terminal")}><span className="tool-icon"><Terminal size={16} /></span><span><strong>{item.title ?? "运行命令"}</strong><small>{item.text ?? String(item.payload?.output ?? "查看输出")}</small></span><ChevronRight size={16} /></button>;
  if (item.kind === "file_change") {
    const add = Number(item.payload?.additions ?? 0); const del = Number(item.payload?.deletions ?? 0);
    return <button className="change-capsule" onClick={() => onInspect("changes")}><FileDiff size={16} /><span>{item.title ?? "文件改动"}</span>{(add || del) ? <small><b>+{add}</b> <i>−{del}</i></small> : null}<ChevronRight size={15} /></button>;
  }
  if (item.kind === "artifact" || item.kind === "image") return <button className="tool-row artifact-row" onClick={() => onInspect("artifacts")}><span className="tool-icon"><Package size={16} /></span><span><strong>{item.title ?? "任务产物"}</strong><small>{item.text || "查看已授权产物"}</small></span><ChevronRight size={16} /></button>;
  return <article className="unknown-event"><AlertCircle size={16} /><span>此项目内容需要更新后查看</span></article>;
}

function RichText({ text, onCopy }: { text: string; onCopy: (message: string) => void }) {
  const blocks = text.split(/(```[\s\S]*?```)/g).filter(Boolean);
  const copy = async (value: string) => {
    try { await navigator.clipboard?.writeText(value); onCopy("已复制"); } catch { onCopy("无法访问剪贴板"); }
  };
  return <>{blocks.map((block, index) => block.startsWith("```") ? <div className="code-block" key={index}><button onClick={() => void copy(block.replace(/^```\w*\n?|```$/g, ""))}><Copy size={14} />复制</button><pre><code>{block.replace(/^```\w*\n?|```$/g, "")}</code></pre></div> : <>{block.split("\n").filter(Boolean).map((line, lineIndex) => <p key={`${index}-${lineIndex}`}>{line}</p>)}</>)}</>;
}

function ApprovalCard({ approval, onRespond }: { approval: Approval; onRespond: (approval: Approval, decision: string) => void }) {
  const command = String(approval.payload?.command ?? approval.payload?.summary ?? "此操作");
  const busy = approval.state === "responding";
  return <article className="approval-card"><div className="approval-heading"><span className="approval-icon"><Shield size={17} /></span><div><strong>{approval.kind === "command" ? "需要运行命令" : approval.kind === "file_change" ? "需要确认文件改动" : "需要你的确认"}</strong><small>{String(approval.payload?.summary ?? "电脑正在等待你的选择。")}</small></div></div><code>{command}</code><div className="approval-actions">{approval.availableDecisions.includes("deny") && <button className="button secondary small" disabled={busy} onClick={() => onRespond(approval, "deny")}>拒绝</button>}{approval.availableDecisions.includes("approve") && <button className="button primary small" disabled={busy} onClick={() => onRespond(approval, "approve")}>{busy ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />}允许</button>}</div></article>;
}

function Composer({ task, settings, host, project, disabled, stopping, onSettings, onSend, onStop, onNotify }: { task: Task; settings?: EffectiveSettings | undefined; host: Host; project: Project; disabled: boolean; stopping: boolean; onSettings: (tab: SettingsTab) => void; onSend: (text: string) => Promise<boolean>; onStop: () => void; onNotify: (message: string) => void }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [composing, setComposing] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const running = isActive(task);
  useEffect(() => {
    const input = textarea.current;
    if (!input) return;
    input.style.height = "0px";
    input.style.height = `${Math.min(150, Math.max(46, input.scrollHeight))}px`;
  }, [text]);
  const submit = async () => {
    const value = text.trim();
    if (!value || sending || disabled) return;
    setSending(true);
    const accepted = await onSend(value);
    if (accepted) setText("");
    setSending(false);
  };
  return <div className="composer-area">
    {disabled && <div className="composer-offline"><WifiOff size={14} />电脑离线；草稿仍保留在此页面</div>}
    <div className="composer-shell">
      <textarea ref={textarea} value={text} disabled={disabled} onChange={(event) => setText(event.target.value)} onCompositionStart={() => setComposing(true)} onCompositionEnd={() => setComposing(false)} onKeyDown={(event) => { if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && !composing) { event.preventDefault(); void submit(); } }} placeholder={running ? "跟进这项任务" : "问问工作区"} aria-label="任务输入" rows={1} />
      <div className="composer-controls"><button className="composer-icon" aria-label="添加附件" onClick={() => onNotify("附件上传将在连接的项目中进行") }><Paperclip size={18} /></button><button className="composer-chip permission" onClick={() => onSettings("permission")}><Shield size={14} /><span>{settings?.permissionLabel ?? "权限"}</span></button><button className="composer-chip model" onClick={() => onSettings("model")}><span>{settings?.modelId ?? "模型"}</span><ChevronDown size={13} /></button><button className="composer-icon desktop-only" aria-label="推理强度" onClick={() => onSettings("reasoning")}><Brain size={17} /></button><button className="composer-icon desktop-only" aria-label="响应速度" onClick={() => onSettings("speed")}><Zap size={17} /></button><button className="composer-icon desktop-only" aria-label="语音输入" onClick={() => onNotify("语音转写需要已配置的主机能力") }><Mic size={17} /></button>{running ? <button className="stop-button" disabled={stopping || disabled} onClick={onStop} aria-label="停止任务">{stopping ? <LoaderCircle className="spin" size={18} /> : <CircleStop size={18} />}</button> : <button className="send-button" disabled={sending || !text.trim() || disabled} onClick={() => void submit()} aria-label="发送">{sending ? <LoaderCircle className="spin" size={18} /> : <ArrowUp size={19} />}</button>}</div>
    </div>
    <div className="composer-meta"><span>{host.name} · {project.name}</span><span>{running ? "电脑继续执行中" : "⌘/Ctrl + Enter 发送"}</span></div>
  </div>;
}

function SettingsSheet({ api, tab, host, project, task, settings, profiles, capabilityRevision, onClose, onSave }: { api: ApiClient; tab: SettingsTab; host: Host; project: Project; task: Task; settings?: EffectiveSettings | undefined; profiles: PermissionProfile[]; capabilityRevision?: string | undefined; onClose: () => void; onSave: (settings: EffectiveSettings) => Promise<void> }) {
  const [active, setActive] = useState<SettingsTab>(tab);
  const [models, setModels] = useState<ModelOption[]>();
  const [value, setValue] = useState<EffectiveSettings>(() => settings ?? { modelId: "", permissionProfileId: "", appliesTo: "next_turn" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { void api.getModels(host.hostId, capabilityRevision).then(setModels).catch(() => setModels([])); }, [api, capabilityRevision, host.hostId]);
  useEffect(() => setActive(tab), [tab]);
  const selectedModel = models?.find((model) => model.modelId === value.modelId) ?? models?.[0];
  const update = (partial: Partial<EffectiveSettings>) => setValue((current) => ({ ...current, ...partial }));
  const save = async () => { setSaving(true); await onSave(value); setSaving(false); onClose(); };
  const labels: Record<SettingsTab, string> = { model: "模型", speed: "速度", reasoning: "推理", permission: "权限" };
  return <Sheet title={labels[active]} onClose={onClose} className="settings-sheet">
    <div className="setting-tabs">{(Object.keys(labels) as SettingsTab[]).map((item) => <button key={item} className={active === item ? "active" : ""} onClick={() => setActive(item)}>{labels[item]}</button>)}</div>
    {active === "model" && <div className="option-list">{models === undefined ? <ListSkeleton rows={2} /> : models.map((model) => <OptionRow key={model.modelId} selected={value.modelId === model.modelId} disabled={!model.available} title={model.label} detail={model.description} onClick={() => update({ modelId: model.modelId, effort: model.defaultEffort, serviceTier: model.defaultServiceTier })} />)}</div>}
    {active === "speed" && <div className="option-list">{(selectedModel?.serviceTiers ?? []).map((tier) => <OptionRow key={tier.id} selected={value.serviceTier === tier.id} title={tier.name} detail={tier.description} icon={<Gauge size={17} />} onClick={() => update({ serviceTier: tier.id })} />)}</div>}
    {active === "reasoning" && <div className="option-list">{(selectedModel?.reasoningEfforts ?? []).map((effort) => <OptionRow key={effort} selected={value.effort === effort} title={effort} detail={effort === "低" ? "更快返回" : effort === "深入" || effort === "最大" ? "更长的处理时间" : "适合大多数任务"} icon={<Brain size={17} />} onClick={() => update({ effort })} />)}</div>}
    {active === "permission" && <div className="option-list">{profiles.map((profile) => <OptionRow key={profile.permissionProfileId} selected={value.permissionProfileId === profile.permissionProfileId} disabled={profile.available === false} title={profile.label} detail={profile.detail} icon={<Shield size={17} />} onClick={() => update({ permissionProfileId: profile.permissionProfileId, permissionLabel: profile.label })} />)}</div>}
    <p className="settings-note">{value.appliesTo === "active_turn" ? "此修改会作用于当前任务。" : "此修改会在下一轮开始时生效。"}</p>
    <button className="button primary full" disabled={saving || !value.modelId || !value.permissionProfileId} onClick={() => void save()}>{saving ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />}保存设置</button>
  </Sheet>;
}

function InspectorSheet({ api, kind, task, onClose, onNotify }: { api: ApiClient; kind: Exclude<InspectorKind, null>; task: Task; onClose: () => void; onNotify: (message: string) => void }) {
  const [changes, setChanges] = useState<ChangeSummary>();
  const [terminal, setTerminal] = useState<TerminalPage>();
  const [artifacts, setArtifacts] = useState<Artifact[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    const fetcher = kind === "changes" ? api.getChanges(task.taskId).then(setChanges) : kind === "terminal" ? api.getTerminal(task.taskId).then(setTerminal) : api.getArtifacts(task.taskId).then(setArtifacts);
    void fetcher.catch((nextError) => setError(readableError(nextError)));
  }, [api, kind, task.taskId]);
  const title = kind === "changes" ? "改动" : kind === "terminal" ? "终端输出" : "产物";
  return <Sheet title={title} onClose={onClose} className="inspector-sheet">
    {error && <InlineError text={error} />}
    {kind === "changes" && (changes ? changes.files.length ? <div className="diff-list">{changes.files.map((file) => <article className="diff-file" key={file.path}><div><FileDiff size={17} /><strong>{file.path}</strong><span>{file.status ?? "modified"}</span></div><small><b>+{file.additions ?? 0}</b><i>−{file.deletions ?? 0}</i></small>{file.patch && <pre>{file.patch}</pre>}</article>)}</div> : <EmptyState title="没有可查看的改动" text="这项任务还没有返回有效 diff。" /> : <ListSkeleton rows={3} />)}
    {kind === "terminal" && (terminal ? <div className="terminal-view"><div className="terminal-title"><Terminal size={15} />{terminal.cwd ?? "终端"}</div><pre>{terminal.lines.map((line) => line.text).join("\n") || "尚无输出"}</pre><div className="terminal-footer">{terminal.exitCode === null ? "任务仍在运行" : terminal.exitCode === undefined ? "等待状态" : `退出码 ${terminal.exitCode}`}{terminal.truncated && " · 输出已截断"}</div></div> : <ListSkeleton rows={4} />)}
    {kind === "artifacts" && (artifacts ? artifacts.length ? <div className="artifact-list">{artifacts.map((artifact) => <button key={artifact.artifactId} className="artifact-item" onClick={() => onNotify("会通过已授权的内容地址打开产物") }><span className="tool-icon"><Package size={17} /></span><span><strong>{artifact.name}</strong><small>{artifact.mime ?? "文件"}{artifact.sizeBytes ? ` · ${formatBytes(artifact.sizeBytes)}` : ""}</small></span><ChevronRight size={16} /></button>)}</div> : <EmptyState title="没有可查看的产物" text="任务生成的文件会在这里出现。" /> : <ListSkeleton rows={2} />)}
  </Sheet>;
}

function SearchSheet({ api, hosts, initialTasks, onClose, onTask }: { api: ApiClient; hosts: Host[]; initialTasks: Task[]; onClose: () => void; onTask: (task: Task) => void }) {
  const [query, setQuery] = useState("");
  const [tasks, setTasks] = useState(initialTasks);
  const [loading, setLoading] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => {
    const host = hosts.find((item) => item.presence === "online") ?? hosts[0];
    if (!host) return;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      try { setTasks((await api.getTasks(host.hostId, { query })).items); } finally { setLoading(false); }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [api, hosts, query]);
  return <div className="search-overlay"><div className="search-header"><label className="search-field expanded"><Search size={18} /><input ref={input} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索任务标题" /><button className={query ? "mini-clear visible" : "mini-clear"} onClick={() => setQuery("")} aria-label="清除"><X size={14} /></button></label><button className="cancel-button" onClick={onClose}>取消</button></div><div className="search-results">{loading && <div className="searching"><LoaderCircle className="spin" size={16} />正在搜索</div>}{tasks.length ? tasks.map((task) => <TaskRow key={task.taskId} task={task} onClick={() => onTask(task)} />) : <EmptyState title="没有匹配结果" text="默认只搜索任务标题。" />}</div></div>;
}

function HostSheet({ hosts, onClose, onPair, onRetry }: { hosts: Host[]; onClose: () => void; onPair: () => void; onRetry: () => void }) {
  return <Sheet title="已连接的电脑" onClose={onClose}>
    <div className="host-list">{hosts.map((host) => <div className="host-row" key={host.hostId}><span className="host-icon"><Monitor size={19} /></span><span><strong>{host.name}</strong><small>{host.platform} · {host.adapterMode === "desktop_attached" ? "桌面已连接" : host.adapterMode === "history_readonly" ? "历史只读" : "独立运行"}</small></span><span className={`presence-label ${host.presence}`}><PresenceDot presence={host.presence} />{host.presence === "online" ? "在线" : host.presence === "stale" ? "重连中" : "离线"}</span></div>)}</div>
    {!hosts.length && <EmptyState title="还没有连接的电脑" text="需要从电脑端开始配对。" />}
    <div className="sheet-actions"><button className="button secondary" onClick={onRetry}><RefreshCw size={16} />刷新</button><button className="button primary" onClick={onPair}><Plus size={16} />连接电脑</button></div>
  </Sheet>;
}

function AppearanceSheet({ theme, onTheme, onClose, onDone }: { theme: Theme; onTheme: (theme: Theme) => void; onClose: () => void; onDone: () => void }) {
  return <Sheet title="外观" onClose={onClose}><div className="appearance-list"><OptionRow title="跟随系统" detail="随设备当前外观变化" selected={theme === "system"} icon={<Settings2 size={17} />} onClick={() => onTheme("system")} /><OptionRow title="浅色" detail="明亮工作区" selected={theme === "light"} icon={<Sun size={17} />} onClick={() => onTheme("light")} /><OptionRow title="深色" detail="低光环境更舒适" selected={theme === "dark"} icon={<Moon size={17} />} onClick={() => onTheme("dark")} /></div><button className="button primary full" onClick={() => { onDone(); onClose(); }}>完成</button></Sheet>;
}

function ProjectInfoSheet({ project, host, onClose, onNew, onNotify }: { project?: Project | undefined; host?: Host | undefined; onClose: () => void; onNew: () => void; onNotify: (message: string) => void }) {
  return <Sheet title="项目详情" onClose={onClose}><div className="fact-list"><Fact icon={<Monitor size={16} />} label="电脑" value={host?.name ?? "未同步"} /><Fact icon={<Folder size={16} />} label="授权路径" value={project?.displayPath ?? "未提供"} /><Fact icon={<Code2 size={16} />} label="Git 分支" value={project?.branch ?? (project?.isGit ? "当前分支" : "不是 Git 项目")} /></div><div className="sheet-actions"><button className="button secondary" onClick={() => onNotify("项目详情已刷新") }><RefreshCw size={16} />刷新</button><button className="button primary" onClick={onNew}><Plus size={16} />新任务</button></div></Sheet>;
}

function TaskMoreSheet({ task, host, project, connection, onClose, onInspect, onNavigate }: { task?: Task | undefined; host?: Host | undefined; project?: Project | undefined; connection: ConnectionState; onClose: () => void; onInspect: (kind: InspectorKind) => void; onNavigate: (route: Route) => void }) {
  return <Sheet title="任务" onClose={onClose}><div className="task-sheet-summary"><span className={`task-state ${task?.runtimeState ?? "idle"}`}><Circle size={8} fill="currentColor" /></span><div><strong>{task?.title ?? "任务"}</strong><small>{statusText(task ?? { runtimeState: "idle" } as Task)} · {connection === "online" || connection === "demo" ? "电脑已连接" : "离线"}</small></div></div><div className="menu-list"><button onClick={() => { onClose(); onInspect("changes"); }}><FileDiff size={18} />查看改动<ChevronRight size={16} /></button><button onClick={() => { onClose(); onInspect("terminal"); }}><Terminal size={18} />查看终端输出<ChevronRight size={16} /></button><button onClick={() => { onClose(); onInspect("artifacts"); }}><Package size={18} />查看产物<ChevronRight size={16} /></button><button onClick={() => project && host && onNavigate({ page: "project", hostId: host.hostId, projectId: project.projectId })}><Folder size={18} />打开项目<ChevronRight size={16} /></button><button><Archive size={18} />归档任务<ChevronRight size={16} /></button></div></Sheet>;
}

function ProjectPicker({ hosts, projects, hostId, projectId, onClose, onSelect }: { hosts: Host[]; projects: Project[]; hostId: string; projectId: string; onClose: () => void; onSelect: (hostId: string, projectId: string) => void }) {
  return <Sheet title="选择运行位置" onClose={onClose}><div className="picker-section"><p className="field-caption">电脑</p>{hosts.map((host) => <OptionRow key={host.hostId} selected={host.hostId === hostId} title={host.name} detail={`${host.platform} · ${host.presence === "online" ? "已连接" : "离线"}`} icon={<Monitor size={17} />} onClick={() => onSelect(host.hostId, "")} />)}</div><div className="picker-section"><p className="field-caption">项目</p>{projects.filter((project) => project.hostId === hostId).map((project) => <OptionRow key={project.projectId} selected={project.projectId === projectId} title={project.name} detail={project.displayPath} icon={<Folder size={17} />} onClick={() => onSelect(hostId, project.projectId)} />)}</div></Sheet>;
}

function OptionRow({ title, detail, selected, disabled, icon, onClick }: { title: string; detail?: string | undefined; selected?: boolean | undefined; disabled?: boolean | undefined; icon?: ReactNode | undefined; onClick: () => void }) {
  return <button className={`option-row ${selected ? "selected" : ""}`} disabled={disabled} onClick={onClick}><span className="option-icon">{icon ?? <Circle size={15} />}</span><span><strong>{title}</strong>{detail && <small>{detail}</small>}</span><span className="option-check">{selected ? <Check size={17} /> : null}</span></button>;
}

function Sheet({ title, children, onClose, className = "" }: { title: string; children: ReactNode; onClose: () => void; className?: string | undefined }) {
  return <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><section className={`sheet ${className}`} role="dialog" aria-modal="true" aria-label={title}><div className="sheet-handle" /><div className="sheet-header"><h2>{title}</h2><button className="icon-button" aria-label="关闭" onClick={onClose}><X size={19} /></button></div>{children}</section></div>;
}

function BottomNav({ active, onHome, onSearch, onNew }: { active: "home"; onHome: () => void; onSearch: () => void; onNew: () => void }) {
  return <nav className="bottom-nav" aria-label="主导航"><button className={active === "home" ? "active" : ""} onClick={onHome}><Sparkles size={20} /><span>工作区</span></button><button onClick={onSearch}><Search size={20} /><span>搜索</span></button><button className="new-nav" onClick={onNew}><span><Plus size={22} /></span><small>新任务</small></button></nav>;
}

function PresenceDot({ presence }: { presence: Host["presence"] }) { return <i className={`presence-dot ${presence}`} aria-hidden="true" />; }

function InlineError({ text, retry }: { text: string; retry?: (() => void) | undefined }) { return <div className="inline-error" role="alert"><AlertCircle size={17} /><span>{text}</span>{retry && <button onClick={retry}>重试</button>}</div>; }

function EmptyState({ title, text, action, onAction }: { title: string; text: string; action?: string | undefined; onAction?: (() => void) | undefined }) { return <div className="empty-state"><span className="empty-icon"><Sparkles size={20} /></span><strong>{title}</strong><p>{text}</p>{action && <button className="button secondary small" onClick={onAction}>{action}</button>}</div>; }

function ListSkeleton({ rows }: { rows: number }) { return <div className="skeleton-list">{Array.from({ length: rows }, (_, index) => <div className="skeleton-row" key={index}><i /><span /><b /></div>)}</div>; }

function ThreadSkeleton() { return <div className="thread-skeleton"><div /><div /><div /><div /></div>; }

function Fact({ icon, label, value }: { icon: ReactNode; label: string; value: string }) { return <div className="fact"><span>{icon}</span><div><small>{label}</small><strong>{value}</strong></div></div>; }

function ArrowDownIcon() { return <ArrowUp size={15} className="arrow-down" />; }

function formatBytes(bytes: number): string { return bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`; }

function readableError(error: unknown): string {
  if (error instanceof ApiError) return error.requestId ? `${error.message}（请求 ${error.requestId}）` : error.message;
  return error instanceof Error ? error.message : "发生了未知错误。";
}
